import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { MessageSquare, X, Send, Sparkles, ArrowUpRight, RotateCcw, FileText, Star, Mic, MicOff, BookMarked, Trash2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import Sparkline from "./assistant/Sparkline";
import { useVoiceInput } from "./assistant/useVoiceInput";

const BURG = "#8B1E1E";

function formatNaira(n) {
  if (n == null) return "";
  try { return "₦" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }); }
  catch { return `₦${n}`; }
}

export default function AssistantFAB() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [proactive, setProactive] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("chat"); // "chat" | "library"
  const [prompts, setPrompts] = useState([]);
  const bottomRef = useRef();

  const roleName = (user?.role_name || user?.role?.name || "").toLowerCase();
  const isCashier = roleName === "cashier";
  const isPOS = location.pathname.startsWith("/pos") || location.pathname.startsWith("/staff") || location.pathname.startsWith("/kiosk") || location.pathname.startsWith("/customer-display") || location.pathname.startsWith("/kds");
  const hidden = !user || isCashier || isPOS;

  const voice = useVoiceInput({
    onTranscript: (t) => { setInput(t); send(t); },
  });

  useEffect(() => {
    if (hidden) return;
    api.get("/assistant/available").then((r) => setAvailable(!!r.available)).catch(() => setAvailable(false));
  }, [hidden, user?.id]);

  useEffect(() => {
    if (!open || !available) return;
    api.get("/assistant/suggestions").then((r) => setSuggestions(r.suggestions || [])).catch(() => {});
    api.get("/assistant/proactive").then((r) => setProactive(r.items || [])).catch(() => {});
    loadPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, available]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (hidden || !available) return null;

  const loadPrompts = () => api.get("/assistant/prompts").then((r) => setPrompts(r.prompts || [])).catch(() => {});

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, content: m.content, context: m.context }));
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    setView("chat");
    try {
      const r = await api.post("/assistant/chat", { message: q, history });
      setMessages((m) => [...m, { role: "assistant", content: r.answer, intent: r.intent, drill_url: r.drill_url, context: r.context, data: r.data, question: q, options: r.options }]);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const savePrompt = async (question) => {
    if (!question) return;
    try {
      await api.post("/assistant/prompts", { text: question, label: question.slice(0, 40) });
      toast.success("Saved to your prompt library");
      loadPrompts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deletePrompt = async (id) => {
    try { await api.del(`/assistant/prompts/${id}`); loadPrompts(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadReport = async (intent, ctx, fmt) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("sm_token");
      const r = await fetch(`${backend}/api/assistant/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ intent, branch: ctx?.branch, period: ctx?.period, format: fmt }),
      });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${intent}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(String(e.message || e)); }
  };

  const toggleVoice = () => {
    if (voice.recording) voice.stop();
    else voice.start();
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full text-white shadow-2xl flex items-center justify-center hover:scale-105 transition"
        style={{ background: BURG }}
        data-testid="assistant-fab"
        aria-label="Open SirMonty Copilot"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-[400px] h-[70vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ background: "#FFFDF9", border: "1px solid #E9DED0" }} data-testid="assistant-panel">
          <div className="px-4 py-3 flex items-center justify-between text-white" style={{ background: BURG }}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {view === "library" ? (
                <button onClick={() => setView("chat")} data-testid="library-back" aria-label="Back to chat"><ChevronLeft size={16} /></button>
              ) : (
                <Sparkles size={15} />
              )}
              <span>{view === "library" ? "Prompt Library" : "SirMonty Copilot"}</span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setView(view === "library" ? "chat" : "library")} title="Prompt Library" data-testid="assistant-library-toggle" aria-label="Prompt library">
                <BookMarked size={15} />
              </button>
              {view === "chat" && (
                <button onClick={() => setMessages([])} title="New conversation" data-testid="assistant-reset" aria-label="New conversation">
                  <RotateCcw size={15} />
                </button>
              )}
            </div>
          </div>

          {view === "library" ? (
            <LibraryView prompts={prompts} onRun={(t) => send(t)} onDelete={deletePrompt} />
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
                {messages.length === 0 && (
                  <>
                    {proactive.length > 0 && (
                      <div className="rounded-2xl p-3 mb-2" style={{ background: "#FFF3D6", border: "1px solid #E9DED0" }}>
                        <div className="text-[10px] tracking-widest uppercase font-semibold mb-1" style={{ color: "#B7791F" }}>Needs Attention</div>
                        {proactive.map((p, i) => (
                          <div key={i} className="text-xs py-1 flex items-center justify-between" style={{ color: "#3B2722" }}>
                            <span>· {p.title}</span>
                            {p.drill_url && <a href={p.drill_url} className="text-[10px] font-semibold" style={{ color: BURG }}>Open</a>}
                          </div>
                        ))}
                      </div>
                    )}
                    {prompts.length > 0 && (
                      <div className="mb-2">
                        <div className="text-[10px] tracking-widest uppercase font-semibold mb-1" style={{ color: "#8B7355" }}>Saved prompts</div>
                        <div className="flex flex-wrap gap-1">
                          {prompts.slice(0, 6).map((p) => (
                            <button
                              key={p.id}
                              onClick={() => send(p.text)}
                              data-testid={`saved-${p.id}`}
                              className="text-[11px] px-2 py-1 rounded-full"
                              style={{ background: "#FFF", border: "1px solid #E9DED0", color: "#3B2722" }}
                              title={p.text}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="text-xs mb-1" style={{ color: "#8B7355" }}>Try one of these:</div>
                    {suggestions.map((s, i) => (
                      <button key={i} onClick={() => send(s)} className="w-full text-left text-xs p-2 rounded-xl hover:bg-[#F5EDDC]" style={{ background: "#FFFFFF", border: "1px solid #E9DED0", color: "#3B2722" }} data-testid={`sug-${i}`}>
                        {s}
                      </button>
                    ))}
                  </>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                    <div className={`inline-block max-w-[85%] text-sm px-3 py-2 rounded-2xl whitespace-pre-wrap ${m.role === "user" ? "text-white" : ""}`}
                         style={{ background: m.role === "user" ? BURG : "#FFFFFF", border: m.role === "user" ? "none" : "1px solid #E9DED0", color: m.role === "user" ? "#FFFFFF" : "#3B2722" }}
                         data-testid={`msg-${i}`}>
                      {m.content}
                      {m.role === "assistant" && m.data?.series?.points?.length > 1 && (
                        <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #E9DED0" }} data-testid={`spark-${i}`}>
                          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#8B7355" }}>{m.data.series.label}</div>
                          <Sparkline points={m.data.series.points} />
                          <div className="flex justify-between text-[10px] mt-0.5" style={{ color: "#8B7355" }}>
                            <span>{m.data.series.points[0]?.x?.slice(5)}</span>
                            <span>{m.data.series.unit === "currency" ? formatNaira(m.data.series.points[m.data.series.points.length - 1]?.y) : m.data.series.points[m.data.series.points.length - 1]?.y}</span>
                            <span>{m.data.series.points[m.data.series.points.length - 1]?.x?.slice(5)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {m.role === "assistant" && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {m.intent === "clarify" && Array.isArray(m.options) && m.options.map((opt, oi) => (
                          <button
                            key={oi}
                            onClick={() => send(`Show me ${opt.toLowerCase()}`)}
                            data-testid={`clarify-${i}-${oi}`}
                            className="text-[11px] px-2 py-1 rounded-full"
                            style={{ background: "#FFF", border: "1px solid #E9DED0", color: "#3B2722" }}
                          >
                            {opt}
                          </button>
                        ))}
                        {m.drill_url && <a href={m.drill_url} className="inline-flex items-center gap-1 text-[10px] font-semibold" style={{ color: BURG }}><ArrowUpRight size={10} /> View details</a>}
                        {m.intent && !["blocked", "clarify"].includes(m.intent) && <>
                          <button onClick={() => downloadReport(m.intent, m.context, "csv")} className="inline-flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#8B7355" }} data-testid={`csv-${i}`}><FileText size={10} /> CSV</button>
                          <button onClick={() => downloadReport(m.intent, m.context, "pdf")} className="inline-flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#8B7355" }} data-testid={`pdf-${i}`}><FileText size={10} /> PDF</button>
                          <button onClick={() => savePrompt(m.question)} className="inline-flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#B7791F" }} data-testid={`save-${i}`}><Star size={10} /> Save</button>
                        </>}
                      </div>
                    )}
                  </div>
                ))}
                {busy && <div className="text-xs" style={{ color: "#8B7355" }}>Thinking…</div>}
                <div ref={bottomRef} />
              </div>

              <div className="px-3 py-2 flex items-center gap-2" style={{ borderTop: "1px solid #E9DED0", background: "#FFFFFF" }}>
                <input value={input} onChange={(e) => setInput(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && send()}
                       placeholder={voice.recording ? "Listening…" : "Ask about sales, cash, inventory…"}
                       className="flex-1 h-10 rounded-xl px-3 text-sm"
                       style={{ background: "#F5EDDC", border: "1px solid #E9DED0", color: "#3B2722" }}
                       data-testid="assistant-input" />
                {voice.supported && (
                  <button
                    onClick={toggleVoice}
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{
                      background: voice.recording ? BURG : "#F5EDDC",
                      color: voice.recording ? "#FFF" : BURG,
                      border: "1px solid #E9DED0",
                    }}
                    data-testid="assistant-mic"
                    aria-label={voice.recording ? "Stop voice input" : "Start voice input"}
                    title={voice.recording ? "Tap to stop" : "Tap to dictate"}
                  >
                    {voice.recording ? <MicOff size={15} /> : <Mic size={15} />}
                  </button>
                )}
                <button onClick={() => send()} disabled={busy} className="w-10 h-10 rounded-xl text-white flex items-center justify-center" style={{ background: BURG }} data-testid="assistant-send">
                  <Send size={15} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function LibraryView({ prompts, onRun, onDelete }) {
  return (
    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="assistant-library">
      {prompts.length === 0 && (
        <div className="text-xs text-center py-8" style={{ color: "#8B7355" }}>
          No saved prompts yet.<br />
          Tap the <Star size={10} className="inline" /> star under any answer to save it here.
        </div>
      )}
      {prompts.map((p) => (
        <div key={p.id} className="rounded-xl p-2 flex items-start gap-2" style={{ background: "#FFF", border: "1px solid #E9DED0" }} data-testid={`prompt-${p.id}`}>
          <button onClick={() => onRun(p.text)} className="flex-1 text-left" data-testid={`prompt-run-${p.id}`}>
            <div className="text-xs font-semibold" style={{ color: "#3B2722" }}>{p.label}</div>
            <div className="text-[11px] mt-0.5" style={{ color: "#8B7355" }}>{p.text}</div>
          </button>
          <button onClick={() => onDelete(p.id)} className="p-1" data-testid={`prompt-del-${p.id}`} aria-label="Delete prompt">
            <Trash2 size={12} color="#8B7355" />
          </button>
        </div>
      ))}
    </div>
  );
}
